// Одно соединение Socket.io на вкладку. События сервера правят кеш TanStack Query:
// строки остатков и заказов обновляются на месте, списки с новыми записями
// перечитываются (с задержкой, чтобы пачка сканов не вызвала шквал запросов).
import { createContext, useContext, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { authStore, logout, refreshToken } from '../api/client.ts';
import { notifyError } from '../lib/notify.ts';
import { ProblemGroup } from './problemGroup.ts';
import type { OrderRow, StockRow } from '../api/types.ts';

type Status = 'connecting' | 'online' | 'offline';
const RealtimeContext = createContext<{ status: Status }>({ status: 'connecting' });
export const useRealtimeStatus = () => useContext(RealtimeContext).status;

function debouncedInvalidator(qc: QueryClient) {
  const pending = new Map<string, QueryKey>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  const invalidate = (key: QueryKey) => {
    pending.set(JSON.stringify(key), key);
    timer ??= setTimeout(() => {
      timer = null;
      const keys = [...pending.values()];
      pending.clear();
      keys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
    }, 400);
  };
  return Object.assign(invalidate, { dispose() {
    if (timer) clearTimeout(timer);
    timer = null;
    pending.clear();
  } });
}

/** Правит все закешированные массивы с ключом, начинающимся с prefix */
function patchLists<T>(qc: QueryClient, prefix: string, rows: T[], match: (a: T, b: T) => boolean, stamp = false): boolean {
  let missing = false;
  for (const [key, data] of qc.getQueriesData<T[]>({ queryKey: [prefix] })) {
    if (!Array.isArray(data)) continue;
    let changed = false;
    const next = data.map((old) => {
      const upd = rows.find((r) => match(old, r));
      if (!upd) return old;
      changed = true;
      return { ...old, ...upd, ...(stamp ? { _changedAt: Date.now() } : {}) };
    });
    if (rows.some((r) => !data.some((old) => match(old, r)))) missing = true;
    if (changed) qc.setQueryData(key, next);
  }
  return missing;
}

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const session = useSyncExternalStore(authStore.subscribe, authStore.version);
  const [status, setStatus] = useState<Status>('connecting');
  const socketRef = useRef<Socket | null>(null);
  const oversellGroup = useRef(new ProblemGroup('problem-oversell',
    (n) => `Оверсейл — ${n} ${n === 1 ? 'заказ' : n < 5 ? 'заказа' : 'заказов'}`));
  const unmappedGroup = useRef(new ProblemGroup('problem-unmapped',
    (n) => `Несопоставленный товар — ${n} ${n === 1 ? 'заказ' : n < 5 ? 'заказа' : 'заказов'}`));

  useEffect(() => {
    const invalidate = debouncedInvalidator(qc);
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    const socket = io({
      path: '/socket.io',
      // HTTP establishes the channel even when a proxy or Wi-Fi blocks the WebSocket upgrade.
      // A failed upgrade leaves HTTP working; retry the other transport if the first is unavailable.
      transports: ['polling', 'websocket'],
      tryAllTransports: true,
      // функция — чтобы при переподключении брался свежий токен
      auth: (cb) => cb({ token: authStore.get().token }),
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
    socketRef.current = socket;
    // Close immediately on an identity/permission change, before an old socket can refill a cleared cache.
    const unsubscribe = authStore.subscribe(() => {
      if (session === authStore.version()) return;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      invalidate.dispose();
      socket.removeAllListeners();
      socket.close();
    });
    let everConnected = false;

    socket.on('connect', () => {
      setStatus('online');
      // после обрыва — перечитать всё открытое: события за время офлайна не пришли
      if (everConnected && !socket.recovered) qc.invalidateQueries();
      everConnected = true;
    });
    socket.on('disconnect', (reason) => {
      setStatus('offline');
      if (reason === 'io server disconnect') reconnectTimer = setTimeout(() => {
        if (session === authStore.version()) socket.connect();
      }, 500);
    });
    socket.on('connect_error', async (err: any) => {
      setStatus('offline');
      const code = err?.data?.code;
      if (code === 'token_expired' || code === 'unauthorized') {
        if (await refreshToken() && session === authStore.version()) socket.connect();
      } else if (code === 'session_revoked') {
        await logout();
      }
    });

    socket.on('stock', (rows: StockRow[]) => {
      invalidate(['stockRelatedKits']);
      invalidate(['buffer-alerts']);
      const same = (a: StockRow, b: StockRow) => a.product_id === b.product_id && a.warehouse_id === b.warehouse_id;
      for (const [key, data] of qc.getQueriesData<StockRow[]>({ queryKey: ['stocks'] })) {
        if (key[1] === 'by-category') {
          if (rows.some((r) => key[2] == null || r.warehouse_id === key[2])) invalidate(key);
          continue;
        }
        const params = (typeof key[1] === 'object' && key[1] !== null ? key[1] : {}) as Record<string, unknown>;
        const relevant = rows.filter((r) => params.warehouse_id == null || r.warehouse_id === Number(params.warehouse_id));
        if (!relevant.length || !Array.isArray(data)) continue;
        // A stock event cannot reconstruct search/category/problem membership. Read filtered lists again.
        const filtered = Object.entries(params).some(([name, value]) => !['warehouse_id', 'limit'].includes(name) && Boolean(value));
        const missing = relevant.some((r) => !data.some((old) => same(old, r)));
        qc.setQueryData(key, data.map((old) => {
          const update = relevant.find((r) => same(old, r));
          return update ? { ...old, ...update, _changedAt: Date.now() } : old;
        }));
        if (filtered || missing) invalidate(key);
      }
      rows.forEach((r) => {
        invalidate(['product', r.product_id]);
        invalidate(['stockCells', r.product_id]);
      });
      invalidate(['products']);
      invalidate(['barcode']);
    });
    socket.on('cells', () => invalidate(['stockCells']));
    socket.on('storage_location', () => {
      invalidate(['storageLocation']);
      invalidate(['stocks']);
      invalidate(['products']);
    });

    socket.on('order', (rows: OrderRow[]) => {
      const missing = patchLists(qc, 'orders', rows, (a, b) => a.id === b.id, true);
      // статус поменялся — заказ мог уйти из отфильтрованного списка или появиться в нём
      invalidate(['orders']);
      if (missing) invalidate(['orders']);
      rows.forEach((r) => invalidate(['order', r.id]));
      invalidate(['pickLists']);
    });
    socket.on('pick_list', (rows: any[]) => {
      patchLists(qc, 'pickLists', rows, (a: any, b: any) => a.id === b.id, true);
      invalidate(['pickLists']);
      rows.forEach((r) => invalidate(['pickList', r.id]));
    });
    socket.on('receipt', (rows: any[]) => {
      invalidate(['receipts']);
      rows.forEach((r) => invalidate(['receipt', r.id]));
    });
    socket.on('customer_return',(rows:any[])=>{
      invalidate(['returnBatches']);invalidate(['customerReturns']);invalidate(['stockDocs']);
      for(const row of rows)if(row.batch_id){invalidate(['returnBatch',row.batch_id]);invalidate(['returnScans',row.batch_id]);}
    });
    socket.on('product', (rows: any[]) => {
      invalidate(['stockRelatedKits']);
      invalidate(['buffer-alerts']);
      invalidate(['products']);
      rows.forEach((r) => invalidate(['product', r.id]));
    });
    socket.on('mapping', () => { invalidate(['stockRelatedKits']); invalidate(['buffer-alerts']); invalidate(['mapping']); invalidate(['unmapped']); invalidate(['product']); });
    socket.on('shipment', (rows: any[]) => {
      invalidate(['shipments']);
      rows.forEach((r) => invalidate(['shipment', r.id]));
    });
    socket.on('supply', (rows: any[]) => {
      invalidate(['supplies']);
      rows.forEach((r) => invalidate(['supply', r.id]));
    });
    socket.on('purchase_order', (rows: any[]) => {
      invalidate(['purchaseOrders']);
      rows.forEach((r) => invalidate(['purchaseOrder', r.id]));
    });
    socket.on('sync_batch', () => { invalidate(['syncBatches']); invalidate(['accounts']); });

    // Складская проблема: тост только сообщает, что появилась/изменилась проблема — сама
    // она хранится и решается в Центре проблем (/problems или /stocks?problems=true),
    // закрытие тоста её не снимает. Группируется в один тост на тип, не на заказ.
    socket.on('oversell', (d: any) => {
      const items = (d.items ?? []) as { product_id: number; free: number }[];
      const contentKey = JSON.stringify(items);
      if (!oversellGroup.current.offer(String(d.id), contentKey)) return; // то же самое событие уже показано
      oversellGroup.current.render(() => 'На маркетплейсы уходит 0 по нехватающим позициям — подробности в «Остатки → Только проблемы».');
    });
    socket.on('unmapped_order', (d: any) => {
      if (unmappedGroup.current.offer(String(d.id), 'unmapped')) {
        unmappedGroup.current.render(() => 'Заказ не зарезервирован, пока нет сопоставления — откройте раздел «Маппинг».');
      }
      invalidate(['unmapped']);
    });
    socket.on('resync', () => qc.invalidateQueries());
    // Закрывает промежуток между первым HTTP-чтением и вступлением в комнату склада.
    socket.on('ready', () => {
      for(const key of ['returnScans','returnBatch','returnBatches','customerReturns']) invalidate([key]);
    });
    socket.on('force_logout', async () => {
      // смена роли — переподключимся с новыми правами; блокировка — refresh не пройдёт
      const refreshed = await refreshToken();
      if (session !== authStore.version()) return;
      if (!refreshed) {
        notifyError('Сессия завершена', 'Администратор завершил сеанс — войдите снова.');
        await logout();
      }
    });

    return () => {
      unsubscribe();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      invalidate.dispose();
      socket.removeAllListeners();
      socket.close();
    };
  }, [qc, session]);

  return <RealtimeContext.Provider value={{ status }}>{children}</RealtimeContext.Provider>;
}
